import {
  ML_PHOTO_GRADE_COLORS,
  ML_PHOTO_GRADE_LABELS,
  PHOTO_GRADE_UNKNOWN_LABEL,
  type MlPhotoGrade,
} from '../lib/ml-sources';

/**
 * 사진 품질 등급 배지 — ML 소스 목록과 점검 상세가 **같은 것**을 쓴다.
 *
 * 등급 코드만 두지 않고 뜻(「결재 완료」·「사진 삭제」)을 함께 보여주는 것이 P5 계약이다
 * (운영자가 「무엇이 학습에 들어가는가」를 알 수 있어야 한다). 좁은 자리(사진 타일 모서리)에서는
 * `compact`로 코드만 그리되 **`title`에 뜻이 남아** 호버로 읽힌다.
 *
 * ⚠️ 색은 라벨을 못 읽어도 **D(삭제)와 A(결재 완료)가 갈려야** 한다는 요구가 정했다 —
 * 팔레트는 `ML_PHOTO_GRADE_COLORS`가 정본이고 여기서 색을 다시 정하지 않는다.
 *
 * ## 등급이 «없이» 올 수 있다 — 그때 빈 알약을 그리지 않는다 (2026-10-07 P4-h)
 *
 * 타입은 필수라 적지만 **배포 순서가 그 약속을 깬다**(admin이 server보다 먼저 올라가는 ~40초
 * 창 — `PHOTO_GRADE_UNKNOWN_LABEL` JSDoc이 숫자로 적는다). 그때 `grade`가 `undefined`로 와서
 * 이 배지가 **문구도 `title`도 `aria-label`도 없는 빈 알약**이 되고 있었다 — 존재는 하는데
 * 아무것도 말하지 않으니 운영자에게는 **보이지 않는 사진**이 된다.
 *
 * ⇒ 없으면 **코드 자리에 「등급 없음」**을 적는다. 등급 하나로 뭉개지(`?? 'C'`) 않는 이유는
 * 없는 성질을 지어내면 운영자가 그 숫자를 믿기 때문이다.
 *
 * ⚠️ **「알 수 없는 등급 코드」와 「등급이 없다」는 다른 것이다.** 서버가 먼저 늘어나 `'E'` 같은
 * 새 코드를 보내면 **그 코드를 그대로** 그린다(존재를 숨기지 않는다 — `derivePurposeTabs`와 같은
 * 규율). 그 둘을 한 분기로 합치면 새 등급이 전부 「등급 없음」으로 보여 **서버가 늘어난 사실이
 * 화면에서 사라진다.**
 */
export function PhotoGradeBadge({
  grade,
  compact = false,
}: {
  /**
   * ⚠️ 옵셔널인 것은 **계약이 아니라 배포 창**이다(위 JSDoc). 응답 타입이 필수라고 해서 이 폭을
   * 좁히지 말 것 — 좁히면 그 창의 데이터가 빈 알약으로 되돌아온다.
   */
  grade: MlPhotoGrade | null | undefined;
  compact?: boolean;
}) {
  const label = grade ? ML_PHOTO_GRADE_LABELS[grade] : undefined;
  // 알 수 없는 등급 코드가 와도(서버가 먼저 늘어난 경우) 코드를 그대로 보여 존재를 숨기지 않는다.
  // 등급 자체가 없으면 코드 자리에 「등급 없음」을 적는다 — 빈 알약을 그리지 않는 것이 요점이다.
  const colorClass = (grade && ML_PHOTO_GRADE_COLORS[grade]) || 'bg-gray-100 text-gray-600';
  const code = grade || PHOTO_GRADE_UNKNOWN_LABEL;
  const title = label ? `${code} · ${label}` : code;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium ${colorClass}`}
      title={title}
      aria-label={title}
    >
      <span className="font-semibold">{code}</span>
      {!compact && label && <span className="font-normal">{label}</span>}
    </span>
  );
}
